import { useState } from 'react';
import { SectionCard } from './section-card';
import { useEveProfile } from '@/hooks/use-eve-profile';

interface EveContextSectionProps {
  isOwner: boolean;
}

export function EveContextSection({ isOwner }: EveContextSectionProps) {
  const { profile, update } = useEveProfile();
  const [text, setText] = useState(profile.freeContext ?? '');
  const [language, setLanguage] = useState(profile.language === 'en' ? 'en' : 'de');
  const [persona, setPersona] = useState(profile.personaMode === 'private' ? 'private' : 'public');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSave() {
    setBusy(true);
    try {
      await update({
        freeContext: text,
        language: language as 'de' | 'en',
        ...(isOwner ? { personaMode: persona as 'public' | 'private' } : {}),
      });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionCard title="EVE-Kontext" description="Was EVE über dich wissen soll + Sprache.">
      <label className="block text-sm uppercase tracking-wide text-slate-400">
        WAS SOLLTE EVE ÜBER DICH WISSEN? (optional)
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSaved(false);
          }}
          rows={4}
          placeholder="z.B. Ich bin Scalper und trade hauptsächlich BTC auf dem 5M Chart. Meine Lieblings-Setups sind Order Blocks mit FVG-Konfluenz. Ich bin erfahrener Trader und schätze direkte, präzise Analysen ohne unnötige Erklärungen."
          className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100"
        />
      </label>
      <p className="text-xs text-slate-500">
        EVE bekommt diesen Kontext bei jedem Gespräch — sie weiß wer du bist und wie sie mit dir
        sprechen soll.
      </p>

      <label className="block text-sm text-slate-300">
        Sprache
        <select
          value={language}
          onChange={(e) => {
            setLanguage(e.target.value);
            setSaved(false);
          }}
          className="mt-1 block rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-slate-100"
        >
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>
      </label>

      {isOwner ? (
        <label className="block text-sm text-slate-300">
          EVE-Persona (Default-Modus)
          <select
            value={persona}
            onChange={(e) => {
              setPersona(e.target.value);
              setSaved(false);
            }}
            className="mt-1 block rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-slate-100"
          >
            <option value="public">Öffentlich (Bloomberg-Ton)</option>
            <option value="private">Privat (Doc-Modus)</option>
          </select>
        </label>
      ) : null}

      <button
        type="button"
        onClick={handleSave}
        disabled={busy}
        className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
      >
        {saved ? 'Gespeichert ✓' : 'Speichern'}
      </button>
    </SectionCard>
  );
}
